import { useCallback, useState } from "react";
import { useAlert } from "src/contexts";
import { AuthserverService } from "src/services/authservers";
import { AuthserverType } from "src/types";

interface UseAuthserverProps {
  organizationId: string;
}

export const useAuthServer = ({ organizationId }: UseAuthserverProps) => {
  const [authservers, setAuthservers] = useState<AuthserverType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);

  const { showAlert } = useAlert();

  const fetchAuthservers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await AuthserverService.getAllAuthservers(
        organizationId
      );
      setAuthservers(response);
    } catch (error: any) {
      if (error.response?.status === 403) {
        showAlert(
          " list",
          error.response.data || "Get Authserver List Failed",
          "error"
        );
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const createAuthserver = async (payload: any) => {
    try {
      setIsLoadingForm(true);
      await AuthserverService.createAuthserver(
        organizationId,
        payload.environmentID,
        payload
      );
      showAlert(
        "Authserver creation",
        "Authserver Created Successfully",
        "success"
      );
      await fetchAuthservers();
      return true;
    } catch (error: any) {
      showAlert(
        "Authserver creation",
        error?.response?.data || "Authserver Creation Failed",
        "error"
      );
      return false;
    } finally {
      setIsLoadingForm(false);
    }
  };

  const updateAuthserver = async (payload: any) => {
    try {
      setIsLoadingForm(true);
      await AuthserverService.updateAuthserver(
        organizationId,
        payload.environmentID,
        payload.id,
        {
          ...payload,
        }
      );
      showAlert(
        "Authserver update",
        "Authserver Updated Successfully",
        "success"
      );
      await fetchAuthservers();
      return true;
    } catch (error: any) {
      showAlert(
        "Authserver update",
        error?.response?.data || "Authserver Update Failed",
        "error"
      );
      return false;
    } finally {
      setIsLoadingForm(false);
    }
  };

  const deleteAuthserver = async ( environmentID: string, authServerId: string) => {
    try {
      setIsLoadingDeletion(true);
      await AuthserverService.deleteAuthserver(
        organizationId,
        environmentID,
        authServerId,
      );
      showAlert(
        "Authserver delete",
        "Authserver Deleted Successfully",
        "success"
      );
      await fetchAuthservers();
      return true;
    } catch (error: any) {
      showAlert(
        "Authserver delete",
        error?.response?.data || "Authserver Deletion Failed",
        "error"
      );
      return false;
    } finally {
      setIsLoadingDeletion(false);
    }
  };

  return {
    isLoading,
    authservers,
    isLoadingForm,
    isLoadingDeletion,
    fetchAuthservers,
    createAuthserver,
    updateAuthserver,
    deleteAuthserver
  };
};
